import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useWallet } from "../../contexts/WalletContext";
import { useTranslation } from "react-i18next";
import {
  FaBars,
  FaTimes,
  FaSignOutAlt,
  FaSignInAlt,
  FaUserPlus,
  FaCog,
  FaBell,
  FaUser,
  FaWallet,
  FaUserShield,
  FaSun,
  FaMoon,
  FaAngleDown,
} from "react-icons/fa";
import { useAuth } from "../../contexts/AuthContext";
import LanguageSwitcher from "../LanguageSwitcher";
import Logo from "../Logo";
import { menuItems } from "../../config/navigation";
import NotificationCenter from "../../notifications/NotificationCenter";
import { useTheme } from "../../contexts/ThemeContext";
import { Sun, Moon, Menu, X } from "lucide-react";
import ThemeToggle from "../ui/ThemeToggle"; // Importer le nouveau composant

/**
 * Composant de navigation principal
 * Utilise les items de menu centralisés et s'adapte à l'authentification
 */
const Navbar = () => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { wallet, connectWallet, disconnectWallet } = useWallet();
  const { user, logout, isAuthenticated } = useAuth();
  const { darkMode, toggleDarkMode } = useTheme();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);

  // Fermer le menu latéral au changement de route sur mobile
  useEffect(() => {
    if (isMobile) {
      setIsOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Détecter le redimensionnement de la fenêtre
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fermer les menus lors des clics en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Obtenir les items de menu visibles
  const visibleMenuItems = React.useMemo(() => {
    const publicItems =
      menuItems?.public?.filter((item) => !item.hideFromNav) || [];
    const privateItems = isAuthenticated
      ? menuItems?.private?.filter((item) => !item.hideFromNav) || []
      : [];
    return [...publicItems, ...privateItems];
  }, [isAuthenticated]);

  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  const handleWalletClick = () => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }

    if (wallet.connected) {
      disconnectWallet();
    } else {
      connectWallet();
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // Animations
  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
      },
    },
  };

  return (
    <>
      {/* Navbar principale */}
      <div className="fixed top-0 left-0 right-0 h-14 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 z-50 shadow-sm">
        <div className="container mx-auto px-4 flex items-center justify-between h-full">
          <div className="flex items-center">
            {/* Bouton hamburger pour mobile */}
            <button
              onClick={() => setIsOpen(true)}
              className="mr-2 lg:hidden p-2 rounded-md hover:bg-gray-200 dark:hover:bg-slate-800"
            >
              <Menu size={24} className="text-gray-700 dark:text-gray-300" />
            </button>

            {/* Logo - unique dans l'application */}
            <Link to="/" className="flex items-center space-x-2">
              <Logo size="small" />
              <span className="text-xl font-semibold text-gray-800 dark:text-white">Baobab Exchange</span>
            </Link>

            {/* Notifications - déplacé à gauche */}
            {isAuthenticated && (
              <div className="relative ml-3" ref={notificationRef}>
                <NotificationCenter />
              </div>
            )}

            {/* Menu horizontal pour desktop */}
            <nav className="hidden lg:flex ml-6 space-x-1">
              {visibleMenuItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                    isActive(item.path)
                      ? "bg-primary text-white"
                      : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  }`}
                >
                  <item.icon size={16} />
                  <span>{t(item.label)}</span>
                </Link>
              ))}
            </nav>
          </div>

          {/* Actions à droite */}
          <div className="flex items-center space-x-3">
            {/* Sélecteur de langue */}
            <LanguageSwitcher />

            {/* Bouton thème */}
            <ThemeToggle />

            {/* Boutons de connexion/déconnexion */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-2 px-3 py-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors duration-150"
                >
                  <div className="w-7 h-7 bg-primary rounded-full flex items-center justify-center text-white">
                    <FaUserShield size={14} />
                  </div>
                  <span className="hidden sm:inline text-sm font-medium text-slate-700 dark:text-slate-200 truncate max-w-[100px]">
                    {user?.fullName?.split(' ')[0] || user?.email?.split('@')[0]}
                  </span>
                  <FaAngleDown className="text-slate-400" size={12} />
                </button>

                <AnimatePresence>
                  {showUserMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 mt-2 w-56 rounded-lg shadow-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 z-50"
                    >
                      <div className="p-2">
                        <div className="px-3 py-2">
                          <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{user.fullName}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{user.email}</p>
                        </div>
                        <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
                        <Link
                          to="/profile"
                          onClick={() => setShowUserMenu(false)}
                          className="block w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors duration-150"
                        >
                          Profil
                        </Link>
                        <Link
                          to="/settings"
                          onClick={() => setShowUserMenu(false)}
                          className="block w-full text-left px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors duration-150"
                        >
                          Paramètres
                        </Link>
                        <div className="border-t border-slate-200 dark:border-slate-700 my-1" />
                        <button
                          onClick={handleLogout}
                          className="flex items-center space-x-2 w-full text-left px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors duration-150"
                        >
                          <FaSignOutAlt size={14} />
                          <span>{t("logout")}</span>
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="hidden lg:flex space-x-2">
                <Link
                  to="/login"
                  className="btn-ghost"
                >
                  {t("login")}
                </Link>
                <Link
                  to="/register"
                  className="btn-primary"
                >
                  {t("register")}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Menu latéral pour mobile */}
      <AnimatePresence>
        {isOpen && isMobile && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/40 z-50 lg:hidden"
            ></motion.div>
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="fixed top-0 left-0 bottom-0 w-72 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 z-50 lg:hidden shadow-xl"
            >
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-800">
                <Link to="/" className="flex items-center space-x-2">
                  <Logo size="small" />
                  <span className="text-xl font-semibold text-gray-800 dark:text-white">Baobab Exchange</span>
                </Link>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 rounded-md hover:bg-gray-200 dark:hover:bg-slate-800"
                >
                  <X size={24} className="text-gray-700 dark:text-gray-300" />
                </button>
              </div>
              <div className="p-4">
                <nav className="flex flex-col space-y-1">
                  {visibleMenuItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center space-x-3 px-3 py-2.5 rounded-md text-base font-medium transition-colors duration-150 ${
                        isActive(item.path)
                          ? "bg-primary text-white"
                          : "text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                      }`}
                    >
                      <item.icon size={20} />
                      <span>{t(item.label)}</span>
                    </Link>
                  ))}
                </nav>
                {/* Boutons de connexion dans la barre latérale mobile */}
                {!isAuthenticated && (
                  <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-700 space-y-2">
                    <Link
                      to="/login"
                      className="btn-ghost w-full justify-center"
                    >
                      {t("login")}
                    </Link>
                    <Link
                      to="/register"
                      className="btn-primary w-full justify-center"
                    >
                      {t("register")}
                    </Link>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
